/**
 * Demo çalışma alanı şeridi (M3 K-M3-01): demo çalışma alanında her sayfada kalıcıdır. Örnek veriler yalnız yöneticinin açık
 * eylemiyle ve yalnız burada yüklenir; tekrar yükleme çoğaltmaz. Demo'da iş verisine bağlı e-posta, davet, ileti ve dış bildirim
 * gönderilmez. Hesap güvenliği bunun dışındadır (v3.8, sahip kararı): "Parolamı unuttum" ile istenen sıfırlama bağlantısı
 * kullanıcının kayıtlı adresine gider — v3.8.1: ortamda sistem postası kapalıysa (`accountSecurityMail: 'off'`) metin bunu söyler.
 *
 * v3.3 demo kabul alanı: örnek veri sürümlüdür. Sunucu yüklenebilir bir şey olduğunu bildirirse (`loadable`) düğme görünür:
 * hiç yüklenmemişse "yükle", eski sürüm yüklüyse "güncelle" (yeni örnekler eklenir, var olanlar çoğalmaz), güncelleme yarıda
 * kaldıysa "sürdür". Eski sunucu yanıtında `loadable` yoktur: o zaman önceki kural geçerlidir (yalnız hiç yüklenmemişse düğme).
 *
 * v3.4: yüklemede ATLANAN örnekler yükleme bittikten sonra da KALICI olarak görünür (hangi örnek, kullanıcı dilinde kısa neden);
 * sunucu `retryable` derse "Atlanan örnekleri yeniden dene" düğmesi vardır — yalnız kullanıcı basınca çalışır, yalnız atlananları
 * dener, var olan kayıtlara dokunmaz, hiçbir şey göndermez; hâlâ eklenemeyen örnek yine atlanmış görünür.
 *
 * v3.6: şerit her genişlikte kısa ve kalıcıdır — başlık + tek cümlelik özet ("veriler hayali; dışarıya hiçbir şey gönderilmez")
 * hep görünür; uzun güvenlik açıklaması "Ayrıntılar" düğmesiyle açılır (`aria-expanded`). Kapalıyken de belgede kalır
 * (`hidden`), yükleme durumu, atlanan örnekler ve eylem düğmeleri gizlenmez.
 *
 * v4.5 (örnek veri sürüm 3): "güncelle" açıklaması yalnız EKSİK sürümlerin getirdiği örnekleri sayar (sürüm 2 yüklüyse yalnız
 * proje ve hizmet teslimi örnekleri). Teslim modülü kapalıyken atlanan örnek, ortak sözlükteki adım ve sebep metniyle görünür.
 *
 * Tema D (21 Ekim 2026): telefonda (390 px) şerit üç satıra çıkıyordu; özet başlıkla tek satıra sığacak kadar kısaldı
 * ("İş verisi gönderilmez." / sistem postası kapalıyken "Hiçbir şey gönderilmez."). Gönderim sınırı özette kalır; hesap
 * güvenliği e-postasının istisnası ve uzun açıklama "Ayrıntılar"dadır. Test kimlikleri değişmedi.
 *
 * v4.6 (CRM sadeleştirme, sahip geri bildirimi "büyük demo açıklaması asıl işi geri plana itiyor"): özet, yükleme durumu,
 * "Ayrıntılar" ve eylem düğmeleri TEK satırdadır (geniş ekranda tek satır; dar ekranda sarar). Gönderim sınırı bilgisi özet
 * cümlesinde kalır; uzun açıklama yine "Ayrıntılar" ile açılır.
 *
 * v4.6 (modül bekleyen örnekler): dayandığı modül bu çalışma alanında açılmadığı için atlanan örnek (sunucunun `waitingForModule`
 * alanı; bugün yalnız proje ve hizmet teslimi) bir sorun değil, beklenen durumdur — modül hakkı yalnız operatör kararıdır ve
 * yeniden deneme o sürece bir şey ekleyemez. Bu yüzden uyarı listesine ve "yeniden dene"ye girmez; şerit tek satır kalır ve
 * "Ayrıntılar" açılınca kısa bir bilgi satırı görünür. Öteki (gerçek) atlamalar için uyarı + düğme aynen kalır. Eski sunucu
 * yanıtında alan yoktur: o zaman bütün atlananlar önceki gibi uyarı olarak gösterilir.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { ChevronDown, FlaskConical } from 'lucide-react';
import {
  PLAN_MODULE_LABELS,
  demoSkipLine,
  demoSkippedList,
  demoUpgradeContent,
  type DemoRetryResult,
  type DemoStatusView,
  type PlanModule,
} from '@castinflow/shared';
import { Button, useToast } from '@castinflow/ui';
import { ApiError, apiFetch } from '../lib/api';
import { apiMutate, useSession } from '../lib/session';
import { qk, useScope } from '../lib/scope';

export const demoText = {
  title: 'Demo çalışma alanı',
  /**
   * Her zaman görünen kısa özet (mobilde de). Tema D: 390 px'te başlıkla TEK satır — gönderim sınırı özette kalır; hesap
   * güvenliği e-postasının istisnası "Ayrıntılar"dadır (`bodyMailOn` / `bodyMailOff`).
   */
  summary: 'İş verisi gönderilmez.',
  /** Ortamda sistem postası kapalıyken (gerçek gönderim kipinde değil): hesap güvenliği e-postası da gitmez */
  summaryMailOff: 'Hiçbir şey gönderilmez.',
  details: 'Ayrıntılar',
  body: 'Bu çalışma alanı yalnız denemek içindir; içindeki şirket, kişi, konuşma, kampanya ve harcama verileri hayalidir. Buradan iş verisiyle ilgili e-posta, davet veya dış bildirim gönderilmez; konuşmalara yazdığınız yanıtlar müşteriye ulaşmaz ve çalışma alanı hiçbir reklam ya da mesajlaşma hesabına bağlı değildir.',
  bodyMailOn:
    'Hesabınızın güvenliği bunun dışındadır: "Parolamı unuttum" ile istediğiniz sıfırlama bağlantısı yalnız hesabınızın kayıtlı adresine gider.',
  bodyMailOff:
    'Bu ortamda sistem postası kapalı olduğundan parola sıfırlama bağlantısı da gönderilmez.',
  load: 'Örnek verileri yükle',
  upgrade: 'Örnek verileri güncelle',
  /** Eksik sürümlerin getirdiği örnekler (sürüm sırasında); liste boşsa (bilinmeyen sürüm) genel metin */
  upgradeHint: (items: readonly string[]) =>
    items.length === 0
      ? 'Yeni örnekler var. Mevcut örnekleriniz çoğaltılmaz.'
      : `Yeni örnekler var: ${items.join('; ')}. Mevcut örnekleriniz çoğaltılmaz.`,
  /** Kullanılmış çalışma alanında ürünün iş kuralı bazı örnekleri reddettiyse (kota, sınır, silinmiş kayıt) */
  skippedTitle:
    'Bazı örnekler eklenemedi. Mevcut kayıtlarınıza dokunulmadı; eklenemeyen örnekler atlandı:',
  retry: 'Atlanan örnekleri yeniden dene',
  retryHint:
    'Yalnız atlanan örnekler denenir; var olan kayıtlarınıza dokunulmaz, hiçbir şey çoğaltılmaz ve dışarıya e-posta, ileti ya da bildirim gönderilmez. Otomatik tekrar yoktur.',
  retryAllDone: 'Atlanan örneklerin hepsi eklendi',
  retrySome: 'Bazı örnekler eklendi; kalanlar hâlâ eklenemedi (nedenleri şeritte)',
  retryNone: 'Atlanan örnekler yine eklenemedi; nedenleri şeritte yazıyor',
  retryNothing: 'Yeniden denenecek atlanan örnek yok',
  retryError: 'Atlanan örnekler yeniden denenemedi',
  /**
   * Modül açılınca eklenebilecek örnekler (uyarı değil, bilgi; yalnız "Ayrıntılar"da). Modül hakkını operatör verir; bu yüzden
   * kullanıcıya bir eylem önerilmez.
   */
  waiting: (module: PlanModule) =>
    module === 'delivery'
      ? 'Proje ve hizmet ile destek örnekleri, bu çalışma alanında Proje ve hizmet modülü açılınca eklenebilir.'
      : `Bazı örnekler, bu çalışma alanında ${PLAN_MODULE_LABELS[module]} modülü açılınca eklenebilir.`,
  resume: 'Yüklemeyi sürdür',
  resumeHint:
    'Örnek veri güncellemesi yarıda kaldı; kaldığı yerden sürdürülebilir (çoğaltma olmaz).',
  loading: 'Örnek veriler yükleniyor…',
  loaded: 'Örnek veriler yüklendi.',
  failed:
    'Örnek veri yüklemesi yarıda kaldı; çoğaltma olmaması için yeniden denenmez. Yeni bir demo çalışma alanı açabilirsiniz.',
  done: 'Örnek veriler yüklendi',
  already: 'Örnek veriler zaten yüklü',
  error: 'Örnek veriler yüklenemedi',
};

export function DemoBanner() {
  const me = useSession((s) => s.me);
  const scope = useScope();
  const qc = useQueryClient();
  const toast = useToast();
  const canManage = me?.capabilities.includes('settings.manage') ?? false;
  const q = useQuery({
    queryKey: scope ? qk.demoStatus(scope) : ['demo-status', 'none'],
    queryFn: () => apiFetch<DemoStatusView>('/demo/status'),
    enabled: scope !== null,
  });
  const load = useMutation({
    mutationFn: () =>
      apiMutate<{ created: boolean; status: DemoStatusView }>('/demo/load', 'POST', {}),
    onSuccess: async (r) => {
      toast({ tone: 'success', title: r.created ? demoText.done : demoText.already });
      // Tüm kapsam sorguları yenilenir (listeler, haber akışı)
      await qc.invalidateQueries();
    },
    onError: async (e) => {
      toast({ tone: 'danger', title: e instanceof ApiError ? e.message : demoText.error });
      if (scope) await qc.invalidateQueries({ queryKey: qk.demoStatus(scope) });
    },
  });
  const retry = useMutation({
    mutationFn: () => apiMutate<DemoRetryResult>('/demo/retry-skipped', 'POST', {}),
    onSuccess: async (r) => {
      // Modül bekleyen örnekler "kalan" sayılmaz (uyarı değil; modül açılınca eklenebilir)
      const waitingSteps = new Set((r.status.waitingForModule ?? []).map((w) => w.step));
      const left = (r.status.skipped ?? []).filter((s) => !waitingSteps.has(s.step)).length;
      toast({
        tone: r.attempted.length === 0 ? 'info' : r.resolved.length === 0 ? 'warning' : 'success',
        title:
          r.attempted.length === 0
            ? demoText.retryNothing
            : left === 0
              ? demoText.retryAllDone
              : r.resolved.length === 0
                ? demoText.retryNone
                : demoText.retrySome,
      });
      await qc.invalidateQueries();
    },
    onError: async (e) => {
      toast({ tone: 'danger', title: e instanceof ApiError ? e.message : demoText.retryError });
      if (scope) await qc.invalidateQueries({ queryKey: qk.demoStatus(scope) });
    },
  });
  const seed = q.data?.seed ?? null;
  // Sunucu bildirirse onun kararı; bildirmezse (eski sunucu) yalnız hiç yüklenmemiş durumda yüklenebilir
  const loadable = q.isSuccess && (q.data.loadable ?? seed === null);
  const mode = seed === null ? 'load' : seed.status === 'loaded' ? 'upgrade' : 'resume';
  // Güncellemenin getireceği örnekler: tamamlanmış sürümden sunucunun güncel sürümüne (eski yanıtta alan yoksa yüklü sürüm)
  const upgradeItems =
    seed === null
      ? []
      : demoUpgradeContent(seed.completedVersion ?? seed.version, q.data?.latestVersion ?? 0);
  // Atlananlar sunucudan (kalıcı); eski sunucu yanıtında alan yoksa yükleme sayılarından türetilir
  const allSkipped = q.data?.skipped ?? (seed === null ? [] : demoSkippedList(seed.counts));
  // v4.6: modül bekleyenler uyarı değil bilgidir ("Ayrıntılar"da); eski sunucu yanıtında alan yoktur → hepsi uyarı (önceki davranış)
  const waiting = q.data?.waitingForModule ?? [];
  const waitingSteps = new Set(waiting.map((w) => w.step));
  const skippedSteps = allSkipped.filter((s) => !waitingSteps.has(s.step));
  const waitingModules = [...new Set(waiting.map((w) => w.module))];
  const retryable = q.isSuccess && q.data.retryable === true;
  // Yalnız sunucu açıkça "kapalı" derse kapalı metni; aksi hâlde "gönderilebilir" (yükleme sırasında metin değişmesin)
  const mailOff = q.data?.accountSecurityMail === 'off';
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  return (
    <div
      role="region"
      aria-label={demoText.title}
      // Tema uyarlaması: şerit sayfa içeriğiyle aynı kapta (kabuk hizalar), kart yarıçapında; alt boşluğu sayfa kabı verir
      className="rounded-card border border-warning/30 bg-warning-subtle px-3 py-2 text-sm text-fg sm:px-4"
      data-testid="demo-banner"
      data-ready={q.isSuccess ? 'evet' : 'hayir'}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <FlaskConical size={18} aria-hidden="true" className="shrink-0" />
        <p className="min-w-0 flex-1 basis-56" data-testid="demo-summary">
          <span className="font-semibold">{demoText.title}</span>
          <span className="text-fg-muted">
            {' · '}
            {mailOff ? demoText.summaryMailOff : demoText.summary}
          </span>
        </p>
        {seed?.status === 'loaded' ? (
          <p className="text-fg-muted" data-testid="demo-seed-loaded">
            {demoText.loaded}
          </p>
        ) : null}
        {skippedSteps.length > 0 ? (
          <span className="font-medium text-warning" data-testid="demo-skipped-summary">
            {skippedSteps.length} örnek atlandı · Ayrıntılarda inceleyin
          </span>
        ) : null}
        {seed?.status === 'loading' && !loadable ? <p>{demoText.loading}</p> : null}
        {seed?.status === 'failed' && !loadable ? <p>{demoText.failed}</p> : null}
        <button
          type="button"
          aria-expanded={open}
          aria-controls={detailsId}
          onClick={() => {
            setOpen((v) => !v);
          }}
          className="-mx-1 inline-flex min-h-control-sm items-center gap-1 rounded px-1 font-medium text-fg underline decoration-fg-subtle underline-offset-2 hover:decoration-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          data-testid="demo-details-toggle"
        >
          {demoText.details}
          <ChevronDown
            size={14}
            aria-hidden="true"
            className={
              open
                ? 'rotate-180 motion-safe:transition-transform'
                : 'motion-safe:transition-transform'
            }
          />
        </button>
        {canManage && loadable ? (
          <Button
            size="sm"
            variant="secondary"
            loading={load.isPending}
            onClick={() => {
              load.mutate();
            }}
            data-testid="demo-load"
            data-mode={mode}
          >
            {mode === 'load'
              ? demoText.load
              : mode === 'upgrade'
                ? demoText.upgrade
                : demoText.resume}
          </Button>
        ) : null}
        {canManage && retryable ? (
          <Button
            size="sm"
            variant="secondary"
            loading={retry.isPending}
            onClick={() => {
              retry.mutate();
            }}
            data-testid="demo-retry-skipped"
          >
            {demoText.retry}
          </Button>
        ) : null}
      </div>
      <div
        id={detailsId}
        hidden={!open}
        className="max-w-prose space-y-1 pb-1 text-fg-muted sm:pl-[1.875rem]"
        data-testid="demo-details"
      >
        <p>
          {demoText.body} {mailOff ? demoText.bodyMailOff : demoText.bodyMailOn}
        </p>
        {seed !== null && waitingModules.length > 0 ? (
          <p data-testid="demo-seed-waiting">
            {waitingModules.map((m) => demoText.waiting(m)).join(' ')}
          </p>
        ) : null}
      </div>
      {seed !== null && skippedSteps.length > 0 ? (
        <div hidden={!open} className="pb-1 sm:pl-[1.875rem]" data-testid="demo-seed-skipped">
          <p>{demoText.skippedTitle}</p>
          <ul className="list-disc space-y-0.5 pl-5" data-testid="demo-seed-skipped-list">
            {skippedSteps.map((s) => (
              <li key={s.step} data-step={s.step} data-reason={s.reason}>
                {demoSkipLine(s)}
              </li>
            ))}
          </ul>
          {canManage && retryable ? (
            <p className="mt-1 text-fg-muted" data-testid="demo-retry-hint">
              {demoText.retryHint}
            </p>
          ) : null}
        </div>
      ) : null}
      {loadable && mode === 'upgrade' ? (
        <p className="pb-1 sm:pl-[1.875rem]" data-testid="demo-seed-upgrade">
          {demoText.upgradeHint(upgradeItems)}
        </p>
      ) : null}
      {loadable && mode === 'resume' ? (
        <p className="pb-1 sm:pl-[1.875rem]" data-testid="demo-seed-resume">
          {demoText.resumeHint}
        </p>
      ) : null}
    </div>
  );
}
